"use client"

import { embedDoVideo, origemDoVideo } from "@/lib/utils/video"
import type { PageBlock, PageTheme } from "@/lib/services/lead-service"
import type React from "react"
import { useState, useEffect, useRef } from "react"
import Image from "next/image"
import { Button } from "@/components/ui/button"
import { ICONE, Icone } from "@/components/design-new/icone"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Barbell, ChatCircleDots, Check, Heartbeat, InstagramLogo, Leaf, Star, Tag, Quotes,
  Compass, ArrowsClockwise, HandsPraying, CalendarBlank, Plant, Mountains, Sparkle,
  Question, ShieldCheck, VideoCamera, ImageSquare, Trophy, TrendUp, MagicWand,
  Lightning, Confetti, Heart, CaretDown,
  PersonSimpleRun, Bicycle, PersonSimpleSwim, BoxingGlove, Users, Clipboard, Laptop,
  Timer, Scales, PersonArmsSpread,
  WhatsappLogo, FacebookLogo, YoutubeLogo, TiktokLogo, XLogo, LinkedinLogo, EnvelopeSimple,
} from "@phosphor-icons/react"
import { TEMA_DEFAULT } from "./block-templates"
import { CaixaDoHtml } from "./caixa-do-html"
import {
  AcordeaoDaPagina,
  AvatarDaPagina,
  AvisoDaPagina,
  BotaoDaPagina,
  CaixaDaPagina,
  CampoDaPagina,
  DivisorDaPagina,
  EstrelasDaPagina,
  ImagemDaPagina,
  OpcaoDaPagina,
  ProgressoDaPagina,
  SecaoDaPagina,
  SeloDaPagina,
  TextoDaPagina,
  motivoDoEnvio,
} from "./pecas-da-pagina"

interface BlockPreviewProps {
  block: PageBlock
  personalInfo?: {
    nome: string
    apelido: string
    foto_url: string | null
  }
  theme?: PageTheme
  isLive?: boolean
  /**
   * Preços que esta página oferta, na ordem em que o comprador vê.
   *
   * A prévia do editor desenha o valor de verdade: antes ela escrevia "Preço
   * definido na configuração", que é texto de quem está escrevendo o código, e
   * não de quem está montando a página.
   */
  precosDaPagina?: { nome: string; valor: number }[]
  onSubmitLead?: (data: { nome: string; email: string; telefone: string }) => Promise<void>
  /** Tracking de etapas do quiz (só na página pública). */
  onQuizEvent?: (ev: { quizId: string; etapa: number; total: number; tipo: "VER_ETAPA" | "CONCLUIU" }) => void
}

// ─── Token helpers ───

export function radiusValue(r: PageTheme["radius"]): number {
  return r === "none" ? 0 : r === "sm" ? 8 : r === "md" ? 12 : r === "lg" ? 20 : 9999
}

export function fontClass(f: PageTheme["font_heading"] | PageTheme["font_body"]): string {
  if (f === "display") return "font-display"
  if (f === "serif") return "font-serif"
  return "font-body"
}

export function containerMaxClass(c: PageTheme["container_max"]): string {
  return c === "sm" ? "max-w-md" : c === "lg" ? "max-w-3xl" : "max-w-xl"
}

export function pageThemeStyle(theme: PageTheme): React.CSSProperties {
  return {
    backgroundColor: theme.background,
    color: theme.foreground,
  }
}

// ─── Block dispatcher ───

export function BlockPreview({ block, personalInfo, theme, isLive, precosDaPagina, onSubmitLead, onQuizEvent }: BlockPreviewProps) {
  const t = theme || TEMA_DEFAULT
  switch (block.tipo) {
    case "hero":
      return <HeroPreview block={block} personalInfo={personalInfo} theme={t} isLive={isLive} />
    case "texto":
      return <TextoPreview block={block} theme={t} />
    case "formulario":
      return <FormularioPreview block={block} theme={t} isLive={isLive} onSubmitLead={onSubmitLead} />
    case "servicos":
      return <ServicosPreview block={block} theme={t} />
    case "depoimentos":
      return <DepoimentosPreview block={block} theme={t} />
    case "galeria":
      return <GaleriaPreview block={block} theme={t} />
    case "cta":
      return <CtaPreview block={block} theme={t} isLive={isLive} />
    case "redes_sociais":
      return <RedesSociaisPreview block={block} theme={t} isLive={isLive} />
    case "espacamento":
      return <div style={{ height: block.props.altura || 32 }} />
    case "checkout":
      return <CheckoutPreview block={block} theme={t} precos={precosDaPagina} />
    case "badge":
      return <BadgePreview block={block} theme={t} />
    case "imagem":
      return <ImagemPreview block={block} theme={t} />
    case "video":
      return <VideoPreview block={block} theme={t} />
    case "beneficios":
      return <BeneficiosPreview block={block} theme={t} />
    case "destaque":
      return <DestaquePreview block={block} theme={t} />
    case "divisor":
      return <DivisorPreview block={block} theme={t} />
    case "faq":
      return <FaqPreview block={block} theme={t} />
    case "quiz":
      return <QuizPreview block={block} theme={t} isLive={isLive} onQuizEvent={onQuizEvent} onSubmitLead={onSubmitLead} />
    case "garantia":
      return <GarantiaPreview block={block} theme={t} />
    case "html":
      return <HtmlPreview block={block} theme={t} isLive={isLive} />
    default:
      return null
  }
}

/* O HTML do personal não entra no documento da página: ele é limpo e desenhado
   numa caixa isolada, sem a origem do app (`caixa-do-html.tsx` explica). */
function HtmlPreview({ block, theme, isLive }: { block: PageBlock; theme: PageTheme; isLive?: boolean }) {
  const html: string = block.props.html || ""
  if (!html.trim()) {
    return (
      <SecaoDaPagina respiro="mais" className="text-center">
        <TextoDaPagina theme={theme} papel="apoio">
          Este bloco está vazio. Cole o seu código no editor.
        </TextoDaPagina>
      </SecaoDaPagina>
    )
  }
  /* O HTML do personal fica na mesma faixa dos outros blocos, mas o conteúdo é
     dele: não há como padronizar o que vem dentro. */
  return <CaixaDoHtml html={html} theme={theme} isLive={isLive} />
}

// ─── Hero ───

function HeroPreview({ block, personalInfo, theme, isLive }: { block: PageBlock; personalInfo?: BlockPreviewProps["personalInfo"]; theme: PageTheme; isLive?: boolean }) {
  const align = block.props.alinhamento || "center"
  const textAlign = align === "left" ? "text-left" : align === "right" ? "text-right" : "text-center"
  const justify = align === "center" ? "justify-center" : align === "right" ? "justify-end" : "justify-start"
  /* Pedir a foto e não ter foto: no editor isso vira um lugar reservado que diz o
     motivo (interruptor ligado sem nada acontecendo parece defeito). Na página no
     ar não desenha nada, porque um avatar vazio para o comprador é pior que
     nenhum. */
  const pediuFoto = !!block.props.mostrar_foto
  const semFoto = pediuFoto && !personalInfo?.foto_url

  return (
    <SecaoDaPagina respiro="mais" className={textAlign}>
      {pediuFoto && personalInfo?.foto_url && (
        <div className={`mb-4 flex ${justify}`}>
          <AvatarDaPagina
            theme={theme}
            src={personalInfo.foto_url}
            alt={personalInfo.apelido || "Personal"}
          />
        </div>
      )}
      {semFoto && !isLive && (
        <div className={`mb-4 flex flex-col items-center gap-1.5 ${justify}`}>
          <span
            className="grid size-[88px] place-items-center rounded-full border border-dashed"
            style={{ borderColor: theme.border, color: theme.muted }}
          >
            <Icone nome={ICONE.aluno} tamanho={32} />
          </span>
          <TextoDaPagina theme={theme} papel="micro">
            Ponha uma foto no seu perfil e ela aparece aqui
          </TextoDaPagina>
        </div>
      )}

      <TextoDaPagina theme={theme} papel="promessa" como="h1" fonte={fontClass(theme.font_heading)}>
        {block.props.titulo}
      </TextoDaPagina>
      {block.props.subtitulo && (
        <TextoDaPagina
          theme={theme}
          papel="corpo"
          cor={theme.muted}
          fonte={fontClass(theme.font_body)}
          className="mt-2"
        >
          {block.props.subtitulo}
        </TextoDaPagina>
      )}
    </SecaoDaPagina>
  )
}

// ─── Texto ───

function TextoPreview({ block, theme }: { block: PageBlock; theme: PageTheme }) {
  const align = block.props.alinhamento || "left"
  /* Três tamanhos vindos da escala, e não `text-sm`/`text-base`/`text-lg` soltos:
     eram nove tamanhos na mesma página. */
  const papel = block.props.tamanho === "sm" ? "apoio" : block.props.tamanho === "lg" ? "secao" : "corpo"

  return (
    <SecaoDaPagina>
      <TextoDaPagina
        theme={theme}
        papel={papel as "apoio" | "secao" | "corpo"}
        como="p"
        alinhamento={align}
        fonte={fontClass(theme.font_body)}
        className="whitespace-pre-wrap"
      >
        {block.props.conteudo}
      </TextoDaPagina>
    </SecaoDaPagina>
  )
}

// ─── Badge / Pill ───

function BadgePreview({ block, theme }: { block: PageBlock; theme: PageTheme }) {
  const align = block.props.alinhamento || "center"
  const justify = align === "left" ? "justify-start" : align === "right" ? "justify-end" : "justify-center"
  const estilo = block.props.estilo || "soft"

  return (
    <div className={`px-4 pt-4 flex ${justify}`}>
      {/* Selo da página: cápsula com texto de 13px, e não 11px. O selo é a
          primeira coisa que a pessoa lê na página, e 11px é tamanho de nota de
          rodapé. */}
      <SeloDaPagina
        theme={theme}
        estilo={estilo === "solid" ? "solido" : estilo === "outline" ? "contorno" : "suave"}
      >
        <Tag size={14} weight="fill" />
        {block.props.texto}
      </SeloDaPagina>
    </div>
  )
}

// ─── Imagem ───

function ImagemPreview({ block, theme }: { block: PageBlock; theme: PageTheme }) {
  const url = block.props.url || ""
  const align = block.props.alinhamento || "center"
  const radius = block.props.arredondado === "full" ? 9999 : radiusValue(block.props.arredondado || "lg")
  const justify = align === "left" ? "justify-start" : align === "right" ? "justify-end" : "justify-center"

  return (
    <SecaoDaPagina>
      <div className={`flex ${justify}`}>
        {/* A foto aparece inteira, na forma dela. A proporção medida no envio
            reserva o espaço antes de ela chegar, e a página não salta; foto
            enviada antes de 26/10/2026 não tem a medida e entra na altura
            dela. Até ali era 16:9 cortando o resto, e o cartaz perdia o topo
            e o pé. Sem foto, a reserva com o ícone continua deitada. */}
        <ImagemDaPagina
          theme={theme}
          src={url}
          alt={block.props.legenda || ""}
          proporcao={url ? block.props.proporcao : "16/9"}
          ajuste="conter"
          className="w-full"
        />
      </div>
      {block.props.legenda && (
        <TextoDaPagina
          theme={theme}
          papel="micro"
          alinhamento="center"
          fonte={fontClass(theme.font_body)}
          className="mt-2"
        >
          {block.props.legenda}
        </TextoDaPagina>
      )}
    </SecaoDaPagina>
  )
}

// ─── Video ───

function VideoPreview({ block, theme }: { block: PageBlock; theme: PageTheme }) {
  /* Uma fonte só para "o que é este link": o regex daqui não conhecia .mov, que
     é o que sai de iPhone e é aceito no envio. */
  const origem = origemDoVideo(block.props.url)
  const embed = embedDoVideo(block.props.url)
  const url = embed || (block.props.url || "").trim()
  const radius = radiusValue(theme.radius)
  const isFile = origem.tipo === "arquivo"

  return (
    <SecaoDaPagina>
      {block.props.titulo && (
        <TextoDaPagina
          theme={theme}
          papel="secao"
          alinhamento="center"
          fonte={fontClass(theme.font_heading)}
          className="mb-3"
        >
          {block.props.titulo}
        </TextoDaPagina>
      )}
      <div
        className="aspect-video w-full overflow-hidden"
        style={{ borderRadius: radius, background: theme.surface, border: `1px solid ${theme.border}` }}
      >
        {url ? (
          isFile ? (
            /* `object-contain`: cortar as beiradas de um vídeo esconde o que a
               pessoa gravou, e vídeo vertical vira uma faixa do meio. */
            <video controls playsInline preload="metadata" src={url} className="size-full object-contain" />
          ) : (
            <iframe
              src={url}
              title={block.props.titulo || "Vídeo"}
              className="size-full"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
            />
          )
        ) : (
          <div className="grid size-full place-items-center" style={{ color: theme.muted }}>
            <Icone nome={ICONE.video} tamanho={32} />
          </div>
        )}
      </div>
    </SecaoDaPagina>
  )
}

// ─── Beneficios ───

/* Exportado: o Select do painel mostra o MESMO desenho que vai para a página.
   Escolher "Bússola" numa lista de palavras e ver outra coisa no resultado é o
   tipo de surpresa que faz a pessoa desistir de mexer. */
export const BENEFICIO_ICONES: Record<string, React.ReactNode> = {
  compass: <Compass size={24} weight="duotone" />,
  refresh: <ArrowsClockwise size={24} weight="duotone" />,
  prayer: <HandsPraying size={24} weight="duotone" />,
  calendar: <CalendarBlank size={24} weight="duotone" />,
  plant: <Plant size={24} weight="duotone" />,
  mountains: <Mountains size={24} weight="duotone" />,
  sparkle: <Sparkle size={24} weight="duotone" />,
  trophy: <Trophy size={24} weight="duotone" />,
  trend: <TrendUp size={24} weight="duotone" />,
  magic: <MagicWand size={24} weight="duotone" />,
  lightning: <Lightning size={24} weight="duotone" />,
  confetti: <Confetti size={24} weight="duotone" />,
  heart: <Heart size={24} weight="duotone" />,
  shield: <ShieldCheck size={24} weight="duotone" />,
  star: <Star size={24} weight="duotone" />,
}

function BeneficiosPreview({ block, theme }: { block: PageBlock; theme: PageTheme }) {
  const itens = block.props.itens || []
  const colunas = Number(block.props.colunas) || 1
  const radius = radiusValue(theme.radius)

  return (
    <SecaoDaPagina >
      {block.props.titulo && (
        <TextoDaPagina
          theme={theme}
          papel="secao"
          alinhamento="center"
          fonte={fontClass(theme.font_heading)}
          className="mb-4"
        >
          {block.props.titulo}
        </TextoDaPagina>
      )}
      <div className={`grid gap-3 ${colunas === 2 ? "grid-cols-2" : "grid-cols-1"}`}>
        {itens.map((item: any, i: number) => (
          <CaixaDaPagina
            key={i}
            theme={theme}
            className={`flex ${colunas === 2 ? "flex-col items-start" : "items-start gap-3"}`}
          >
            <div
              className="w-12 h-12 flex items-center justify-center shrink-0 mb-3"
              style={{
                background: `${theme.primary}26`,
                color: theme.primary === theme.background ? theme.foreground : theme.primary,
                borderRadius: radiusValue("md"),
              }}
            >
              {BENEFICIO_ICONES[item.icone] || <Sparkle size={24} weight="duotone" />}
            </div>
            <div className={colunas === 2 ? "" : "flex-1"}>
              <TextoDaPagina theme={theme} papel="corpo" como="h3" fonte={fontClass(theme.font_heading)} className="font-semibold">
                {item.titulo}
              </TextoDaPagina>
              {item.descricao && (
                <TextoDaPagina theme={theme} papel="apoio" fonte={fontClass(theme.font_body)}>
                  {item.descricao}
                </TextoDaPagina>
              )}
            </div>
          </CaixaDaPagina>
        ))}
      </div>
    </SecaoDaPagina>
  )
}

// ─── Destaque ───

function DestaquePreview({ block, theme }: { block: PageBlock; theme: PageTheme }) {
  const radius = radiusValue(theme.radius)
  const estilo = block.props.estilo || "soft"

  const containerStyle: React.CSSProperties =
    estilo === "outline"
      ? { background: "transparent", border: `2px solid ${theme.primary}`, color: theme.foreground }
      : estilo === "solid"
        ? { background: theme.primary, color: theme.primary_fg, border: "none" }
        : { background: `${theme.primary}1F`, border: `1px solid ${theme.primary}40`, color: theme.foreground }

  return (
    <SecaoDaPagina >
      <div className="p-5 text-center" style={{ ...containerStyle, borderRadius: radius }}>
        <div className="flex justify-center mb-2">
          <Quotes size={20} weight="fill" style={{ opacity: 0.7 }} />
        </div>
        {block.props.titulo && (
          <TextoDaPagina theme={theme} papel="corpo" como="h3" fonte={fontClass(theme.font_heading)} className="mb-1 font-semibold">{block.props.titulo}</TextoDaPagina>
        )}
        <TextoDaPagina theme={theme} papel="apoio" fonte={fontClass(theme.font_body)}>{block.props.conteudo}</TextoDaPagina>
      </div>
    </SecaoDaPagina>
  )
}

// ─── Divisor ───

function DivisorPreview({ block, theme }: { block: PageBlock; theme: PageTheme }) {
  const estilo = block.props.estilo || "linha"

  if (estilo === "ornamento") {
    const Icon = block.props.ornamento === "sparkle" ? Sparkle : block.props.ornamento === "heart" ? Heart : Star
    return (
      <SecaoDaPagina className="flex items-center gap-3">
        <DivisorDaPagina theme={theme} className="flex-1" />
        <Icon size={16} style={{ color: theme.muted }} weight="fill" aria-hidden />
        <DivisorDaPagina theme={theme} className="flex-1" />
      </SecaoDaPagina>
    )
  }

  return (
    <SecaoDaPagina>
      <DivisorDaPagina theme={theme} />
    </SecaoDaPagina>
  )
}

// ─── FAQ ───

function FaqPreview({ block, theme }: { block: PageBlock; theme: PageTheme }) {
  const itens = block.props.itens || []

  return (
    <SecaoDaPagina >
      {block.props.titulo && (
        <TextoDaPagina
          theme={theme}
          papel="secao"
          alinhamento="center"
          fonte={fontClass(theme.font_heading)}
          className="mb-3"
        >
          {block.props.titulo}
        </TextoDaPagina>
      )}
      {/* Acordeão da página: mesma forma e mesmo comportamento do sistema (mais
          e menos girando, uma seção por vez, alvo de 48px, foco visível), com as
          cores do personal. O que havia aqui era uma seta de 14px, sem foco e
          com alvo de 40px. */}
      <AcordeaoDaPagina
        theme={theme}
        fonteTitulo={fontClass(theme.font_heading)}
        fonteCorpo={fontClass(theme.font_body)}
        itens={itens.map((item: any, i: number) => ({
          id: `faq-${i}`,
          titulo: item.pergunta,
          conteudo: item.resposta,
        }))}
      />
    </SecaoDaPagina>
  )
}

// ─── Quiz ───

function QuizPreview({
  block,
  theme,
  isLive,
  onQuizEvent,
  onSubmitLead,
}: {
  block: PageBlock
  theme: PageTheme
  isLive?: boolean
  onQuizEvent?: (ev: { quizId: string; etapa: number; total: number; tipo: "VER_ETAPA" | "CONCLUIU" }) => void
  onSubmitLead?: (data: { nome: string; email: string; telefone: string }) => Promise<void>
}) {
  const etapas: any[] = block.props.etapas || []
  const total = etapas.length
  const radius = radiusValue(theme.radius)
  const btnRadius = radiusValue(theme.radius === "full" ? "full" : theme.radius)
  const acaoFinal: string = block.props.acao_final || "botao"

  const [step, setStep] = useState(0)
  const [respostas, setRespostas] = useState<Record<string, string[]>>({})
  const firedRef = useRef<Set<string>>(new Set())
  // Formulário da tela final (acao_final === "formulario")
  const [formData, setFormData] = useState({ nome: "", email: "", telefone: "" })
  const [enviandoLead, setEnviandoLead] = useState(false)
  const [leadEnviado, setLeadEnviado] = useState(false)
  const [erroDoLead, setErroDoLead] = useState<string | null>(null)

  // Tracking: VER_ETAPA por etapa (uma vez) + CONCLUIU na tela final
  useEffect(() => {
    if (!isLive || !onQuizEvent || total === 0) return
    if (step < total) {
      const key = `v${step}`
      if (!firedRef.current.has(key)) {
        firedRef.current.add(key)
        onQuizEvent({ quizId: block.id, etapa: step, total, tipo: "VER_ETAPA" })
      }
    } else if (!firedRef.current.has("done")) {
      firedRef.current.add("done")
      onQuizEvent({ quizId: block.id, etapa: total, total, tipo: "CONCLUIU" })
    }
  }, [step, isLive, onQuizEvent, total, block.id])

  if (total === 0) {
    return (
      <SecaoDaPagina respiro="mais" className="text-center">
        <TextoDaPagina theme={theme} papel="apoio">
          Este quiz ainda não tem perguntas. Escreva a primeira no editor.
        </TextoDaPagina>
      </SecaoDaPagina>
    )
  }

  const concluirCta = () => {
    if (!isLive) return
    const link = acaoFinal === "checkout" ? "#checkout" : (block.props.cta_link || "#checkout")
    if (link.startsWith("#")) {
      document.getElementById(link.slice(1))?.scrollIntoView({ behavior: "smooth" })
    } else {
      window.open(link, "_blank")
    }
  }

  const enviarLeadFinal = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!isLive || !onSubmitLead || enviandoLead) return
    setEnviandoLead(true)
    setErroDoLead(null)
    try {
      await onSubmitLead(formData)
      setLeadEnviado(true)
    } catch (falha) {
      /* Falhou, a página diz o motivo (27/10/2026): antes o erro sumia, e o
         visitante ficava sem saber se o contato tinha chegado. */
      setErroDoLead(motivoDoEnvio(falha))
    } finally {
      setEnviandoLead(false)
    }
  }

  // Tela final
  if (step >= total) {
    const inputStyle: React.CSSProperties = {
      background: theme.background,
      border: `1px solid ${theme.border}`,
      color: theme.foreground,
      borderRadius: radiusValue(theme.radius === "full" ? "lg" : theme.radius),
    }
    return (
      <SecaoDaPagina respiro="mais" id={`quiz-${block.id}`}>
        <CaixaDaPagina theme={theme} folgada className="space-y-4 text-center">
          {block.props.imagem_final_url ? (
            <ImagemDaPagina
              theme={theme}
              src={block.props.imagem_final_url}
              alt=""
              proporcao={block.props.imagem_final_proporcao}
              ajuste="conter"
              className="w-full"
            />
          ) : (
            <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto" style={{ background: theme.primary }}>
              <Check className="w-6 h-6" style={{ color: theme.primary_fg }} />
            </div>
          )}
          {block.props.texto_final && (
            <TextoDaPagina theme={theme} papel="apoio" fonte={fontClass(theme.font_body)}>
                {block.props.texto_final}
              </TextoDaPagina>
          )}

          {acaoFinal === "formulario" ? (
            leadEnviado ? (
              <TextoDaPagina theme={theme} papel="corpo" fonte={fontClass(theme.font_body)} className="font-medium">
                {block.props.form_sucesso || "Pronto! Em breve entramos em contato."}
              </TextoDaPagina>
            ) : (
              <form onSubmit={enviarLeadFinal} className="space-y-2.5 text-left">
                {block.props.form_titulo && (
                  <TextoDaPagina theme={theme} papel="corpo" alinhamento="center" fonte={fontClass(theme.font_heading)} className="font-semibold">
                    {block.props.form_titulo}
                  </TextoDaPagina>
                )}
                <CampoDaPagina
                  theme={theme}
                  id={`quiz-${block.id}-nome`}
                  rotulo="Nome"
                  placeholder="Seu nome completo"
                  valor={formData.nome}
                  onChange={(v) => setFormData((p) => ({ ...p, nome: v }))}
                  obrigatorio
                  desativado={!isLive}
                />
                <CampoDaPagina
                  theme={theme}
                  id={`quiz-${block.id}-email`}
                  rotulo="E-mail"
                  tipo="email"
                  teclado="email"
                  placeholder="seu@email.com"
                  valor={formData.email}
                  onChange={(v) => setFormData((p) => ({ ...p, email: v }))}
                  obrigatorio
                  desativado={!isLive}
                />
                <CampoDaPagina
                  theme={theme}
                  id={`quiz-${block.id}-telefone`}
                  rotulo="Telefone"
                  tipo="tel"
                  teclado="tel"
                  placeholder="(00) 00000-0000"
                  valor={formData.telefone}
                  onChange={(v) => setFormData((p) => ({ ...p, telefone: v }))}
                  desativado={!isLive}
                />
                {erroDoLead && <AvisoDaPagina theme={theme}>{erroDoLead}</AvisoDaPagina>}
                <BotaoDaPagina theme={theme} type="submit" desativado={!isLive} carregando={enviandoLead}>
                  {block.props.form_botao || "Enviar"}
                </BotaoDaPagina>
              </form>
            )
          ) : (
            <BotaoDaPagina theme={theme} onClick={concluirCta}>
              {block.props.cta_texto || (acaoFinal === "checkout" ? "Comprar agora" : "Continuar")}
            </BotaoDaPagina>
          )}
        </CaixaDaPagina>
      </SecaoDaPagina>
    )
  }

  const etapa = etapas[step]
  const sel: string[] = respostas[etapa.id] || []
  const multi = etapa.tipo === "multipla"
  const podeAvancar = sel.length > 0

  const toggleOpcao = (opId: string) => {
    setRespostas((r) => {
      const atual = r[etapa.id] || []
      if (multi) {
        return { ...r, [etapa.id]: atual.includes(opId) ? atual.filter((x) => x !== opId) : [...atual, opId] }
      }
      return { ...r, [etapa.id]: [opId] }
    })
  }

  const progresso = Math.round(((step + 1) / total) * 100)

  return (
    <SecaoDaPagina respiro="mais" id={`quiz-${block.id}`}>
      <CaixaDaPagina theme={theme} folgada className="space-y-4">
        {step === 0 && (block.props.titulo || block.props.subtitulo) && (
          <div className="text-center space-y-1">
            {block.props.titulo && (
              <TextoDaPagina theme={theme} papel="secao" como="h2" fonte={fontClass(theme.font_heading)}>{block.props.titulo}</TextoDaPagina>
            )}
            {block.props.subtitulo && (
              <TextoDaPagina theme={theme} papel="apoio" fonte={fontClass(theme.font_body)}>
                {block.props.subtitulo}
              </TextoDaPagina>
            )}
          </div>
        )}

        {/* Quanto falta, com o número escrito: a barra sozinha não diz se
            sobram duas perguntas ou dez. */}
        <ProgressoDaPagina
          theme={theme}
          parte={step + 1}
          total={total}
          rotulo={`Pergunta ${step + 1} de ${total}`}
        />

        {/* Imagem da etapa */}
        {etapa.imagem_url && (
          <ImagemDaPagina
            theme={theme}
            src={etapa.imagem_url}
            alt=""
            proporcao={etapa.imagem_proporcao}
            ajuste="conter"
            className="w-full"
          />
        )}

        {/* Pergunta */}
        <div className="space-y-1">
          <TextoDaPagina theme={theme} papel="corpo" fonte={fontClass(theme.font_heading)} className="font-semibold">{etapa.pergunta}</TextoDaPagina>
          {etapa.descricao && (
            <TextoDaPagina theme={theme} papel="apoio" fonte={fontClass(theme.font_body)}>
                {etapa.descricao}
              </TextoDaPagina>
          )}
          {multi && <p className="text-[0.625rem]" style={{ color: theme.muted }}>Pode escolher mais de uma.</p>}
        </div>

        {/* Opções */}
        <div className="space-y-2">
          {(etapa.opcoes || []).map((op: any) => {
            const ativo = sel.includes(op.id)
            return (
              <OpcaoDaPagina
                key={op.id}
                theme={theme}
                marcada={ativo}
                varias={multi}
                onClick={() => toggleOpcao(op.id)}
                fonte={fontClass(theme.font_body)}
              >
                {op.texto}
              </OpcaoDaPagina>
            )
          })}
        </div>

        {/* Navegação */}
        <div className="flex items-center gap-2">
          {step > 0 && (
            <BotaoDaPagina
              theme={theme}
              tom="contorno"
              larguraTotal={false}
              onClick={() => setStep((s) => s - 1)}
            >
              Voltar
            </BotaoDaPagina>
          )}
          <BotaoDaPagina
            theme={theme}
            desativado={!podeAvancar}
            onClick={() => podeAvancar && setStep((s) => s + 1)}
            className="flex-1"
          >
            {step === total - 1 ? (block.props.cta_texto || "Ver resultado") : (block.props.botao_texto || "Continuar")}
          </BotaoDaPagina>
        </div>
      </CaixaDaPagina>
    </SecaoDaPagina>
  )
}

// ─── Garantia ───

function GarantiaPreview({ block, theme }: { block: PageBlock; theme: PageTheme }) {
  const radius = radiusValue(theme.radius)
  const Icon = block.props.icone === "trophy" ? Trophy : block.props.icone === "star" ? Star : ShieldCheck

  return (
    <SecaoDaPagina >
      {/* A garantia é a caixa que tira o medo de comprar: entra destacada, com a
          borda na cor do personal. */}
      <CaixaDaPagina theme={theme} destacada className="flex items-center gap-3">
        <div
          className="w-12 h-12 flex items-center justify-center shrink-0"
          style={{
            background: `${theme.primary}26`,
            color: theme.primary === theme.background ? theme.foreground : theme.primary,
            borderRadius: 9999,
          }}
        >
          <Icon size={24} weight="duotone" />
        </div>
        <div>
          <TextoDaPagina theme={theme} papel="corpo" como="h3" fonte={fontClass(theme.font_heading)} className="font-semibold">
                {block.props.titulo}
              </TextoDaPagina>
          <TextoDaPagina theme={theme} papel="apoio" fonte={fontClass(theme.font_body)}>
                {block.props.descricao}
              </TextoDaPagina>
        </div>
      </CaixaDaPagina>
    </SecaoDaPagina>
  )
}

// ─── Formulário ───

function FormularioPreview({
  block,
  theme,
  isLive,
  onSubmitLead,
}: {
  block: PageBlock
  theme: PageTheme
  isLive?: boolean
  onSubmitLead?: (data: { nome: string; email: string; telefone: string }) => Promise<void>
}) {
  const [formData, setFormData] = useState({ nome: "", email: "", telefone: "" })
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const radius = radiusValue(theme.radius)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!isLive || !onSubmitLead || loading) return
    setLoading(true)
    setErro(null)
    try {
      await onSubmitLead(formData)
      setSubmitted(true)
    } catch (falha) {
      /* Falhou, a página diz o motivo (27/10/2026): antes o erro sumia, e o
         visitante ficava sem saber se o contato tinha chegado. */
      setErro(motivoDoEnvio(falha))
    } finally {
      setLoading(false)
    }
  }

  if (submitted) {
    return (
      <SecaoDaPagina id="formulario" >
        <CaixaDaPagina theme={theme} folgada className="text-center">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
            style={{ background: theme.primary }}
          >
            <Check className="w-6 h-6" style={{ color: theme.primary_fg }} />
          </div>
          <TextoDaPagina theme={theme} papel="corpo" fonte={fontClass(theme.font_body)} className="font-medium">
            {block.props.mensagem_sucesso}
          </TextoDaPagina>
        </CaixaDaPagina>
      </SecaoDaPagina>
    )
  }

  return (
    <SecaoDaPagina id="formulario" >
      <CaixaDaPagina theme={theme} folgada>
        {block.props.titulo && (
          <TextoDaPagina
          theme={theme}
          papel="secao"
          alinhamento="center"
          fonte={fontClass(theme.font_heading)}
          className="mb-3"
        >
          {block.props.titulo}
        </TextoDaPagina>
        )}
        <form onSubmit={handleSubmit} className="space-y-3">
          {(block.props.campos || ["nome", "email", "telefone"]).map((campo: string) => (
            <CampoDaPagina
              key={campo}
              theme={theme}
              id={`formulario-${campo}`}
              rotulo={campo === "nome" ? "Nome completo" : campo === "email" ? "E-mail" : "Telefone"}
              tipo={campo === "email" ? "email" : campo === "telefone" ? "tel" : "text"}
              teclado={campo === "email" ? "email" : campo === "telefone" ? "tel" : undefined}
              placeholder={
                campo === "nome" ? "Seu nome completo" : campo === "email" ? "seu@email.com" : "(00) 00000-0000"
              }
              valor={(formData as any)[campo] || ""}
              onChange={(v) => setFormData((prev) => ({ ...prev, [campo]: v }))}
              obrigatorio
              desativado={!isLive}
            />
          ))}
          {erro && <AvisoDaPagina theme={theme}>{erro}</AvisoDaPagina>}
          <BotaoDaPagina theme={theme} type="submit" desativado={!isLive} carregando={loading}>
            {block.props.texto_botao || "Enviar"}
          </BotaoDaPagina>
        </form>
      </CaixaDaPagina>
    </SecaoDaPagina>
  )
}

// ─── Serviços ───

/* Os desenhos que o personal pode escolher para cada serviço. A lista tinha
   quatro, e três deles vinham dos exemplos: quem vendia avaliação, online ou
   treino em grupo ficava com a estrela genérica. */
export const SERVICO_ICONES: Record<string, React.ReactNode> = {
  dumbbell: <Barbell size={20} />,
  "heart-pulse": <Heartbeat size={20} />,
  salad: <Leaf size={20} />,
  corrida: <PersonSimpleRun size={20} />,
  bike: <Bicycle size={20} />,
  natacao: <PersonSimpleSwim size={20} />,
  luta: <BoxingGlove size={20} />,
  grupo: <Users size={20} />,
  avaliacao: <Scales size={20} />,
  online: <Laptop size={20} />,
  ficha: <Clipboard size={20} />,
  alongamento: <PersonArmsSpread size={20} />,
  tempo: <Timer size={20} />,
  star: <Star size={20} />,
}

function ServicosPreview({ block, theme }: { block: PageBlock; theme: PageTheme }) {
  const itens = block.props.itens || []
  const radius = radiusValue(theme.radius)

  return (
    <SecaoDaPagina >
      {block.props.titulo && (
        <TextoDaPagina
          theme={theme}
          papel="secao"
          alinhamento="center"
          fonte={fontClass(theme.font_heading)}
          className="mb-3"
        >
          {block.props.titulo}
        </TextoDaPagina>
      )}
      <div className="space-y-2">
        {itens.map((item: any, i: number) => (
          <CaixaDaPagina key={i} theme={theme} className="flex items-start gap-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
              style={{
                background: `${theme.primary}1F`,
                color: theme.primary === theme.background ? theme.foreground : theme.primary,
              }}
            >
              {SERVICO_ICONES[item.icone] || <Star size={20} />}
            </div>
            <div>
              <TextoDaPagina theme={theme} papel="corpo" como="h3" fonte={fontClass(theme.font_heading)} className="font-semibold">
                {item.titulo}
              </TextoDaPagina>
              {item.descricao && (
                <TextoDaPagina theme={theme} papel="apoio" fonte={fontClass(theme.font_body)}>
                {item.descricao}
              </TextoDaPagina>
              )}
            </div>
          </CaixaDaPagina>
        ))}
      </div>
    </SecaoDaPagina>
  )
}

// ─── Depoimentos ───

function DepoimentosPreview({ block, theme }: { block: PageBlock; theme: PageTheme }) {
  const itens = block.props.itens || []
  const radius = radiusValue(theme.radius)

  return (
    <SecaoDaPagina >
      {block.props.titulo && (
        <TextoDaPagina
          theme={theme}
          papel="secao"
          alinhamento="center"
          fonte={fontClass(theme.font_heading)}
          className="mb-3"
        >
          {block.props.titulo}
        </TextoDaPagina>
      )}
      <div className="space-y-2">
        {itens.map((item: any, i: number) => {
          const estrelas = Number(item.estrelas) || 0
          return (
            <CaixaDaPagina key={i} theme={theme}>
              {/* A nota vai também escrita no `aria-label` da peça: cinco
                  desenhos iguais não são lidos por leitor de tela, e quem não
                  distingue as cores não conta as cheias. */}
              {estrelas > 0 && (
                <div className="mb-1.5">
                  <EstrelasDaPagina theme={theme} nota={estrelas} />
                </div>
              )}
              <TextoDaPagina theme={theme} papel="apoio" fonte={fontClass(theme.font_body)} className="mb-2 italic">
                &ldquo;{item.texto}&rdquo;
              </TextoDaPagina>
              <TextoDaPagina theme={theme} papel="micro" className="font-medium">
                {item.nome}
              </TextoDaPagina>
            </CaixaDaPagina>
          )
        })}
      </div>
    </SecaoDaPagina>
  )
}

// ─── Galeria ───

function GaleriaPreview({ block, theme }: { block: PageBlock; theme: PageTheme }) {
  const imagens = block.props.imagens || []
  const colunas = Number(block.props.colunas) || 2
  const radius = radiusValue(theme.radius)
  const gridClass = colunas === 3 ? "grid-cols-3" : "grid-cols-2"

  return (
    <SecaoDaPagina >
      {block.props.titulo && (
        <TextoDaPagina
          theme={theme}
          papel="secao"
          alinhamento="center"
          fonte={fontClass(theme.font_heading)}
          className="mb-1"
        >
          {block.props.titulo}
        </TextoDaPagina>
      )}
      {block.props.descricao && (
        <TextoDaPagina theme={theme} papel="apoio" alinhamento="center" fonte={fontClass(theme.font_body)} className="mb-3">
          {block.props.descricao}
        </TextoDaPagina>
      )}
      {imagens.length === 0 ? (
        <div className={`grid gap-2 ${gridClass}`}>
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="aspect-square flex items-center justify-center"
              style={{ background: theme.surface, border: `1px dashed ${theme.border}`, borderRadius: radius }}
            >
              <ImageSquare size={20} style={{ color: theme.muted }} />
            </div>
          ))}
        </div>
      ) : (
        <div className={`grid gap-2 ${gridClass}`}>
          {imagens.map((img: string, i: number) => (
            <ImagemDaPagina
              key={i}
              theme={theme}
              src={img}
              alt={`Foto ${i + 1} da galeria`}
              proporcao="1/1"
              className="w-full"
            />
          ))}
        </div>
      )}
    </SecaoDaPagina>
  )
}

// ─── CTA ───

function CtaPreview({ block, theme, isLive }: { block: PageBlock; theme: PageTheme; isLive?: boolean }) {
  const isPrimario = block.props.estilo !== "secundario"
  /* Dois tamanhos, e não três: o "sm" tinha 36px de altura, abaixo do alvo de
     dedo, num botão que é o que faz a pessoa comprar. */
  const tamanho = block.props.tamanho || "lg"

  const handleClick = () => {
    if (!isLive) return
    const link = block.props.link || "#"
    if (link.startsWith("#")) {
      const el = document.getElementById(link.replace("#", ""))
      el?.scrollIntoView({ behavior: "smooth" })
    } else {
      window.open(link, "_blank")
    }
  }

  return (
    <SecaoDaPagina className="text-center" >
      <BotaoDaPagina
        theme={theme}
        tom={isPrimario ? "principal" : "contorno"}
        tamanho={tamanho === "lg" ? "grande" : "normal"}
        desativado={!isLive}
        onClick={handleClick}
        className={fontClass(theme.font_body)}
      >
        {block.props.texto || "Clique aqui"}
      </BotaoDaPagina>
    </SecaoDaPagina>
  )
}

// ─── Redes Sociais ───

/*
  Cada rede com o SEU logo, e não as siglas de texto que havia aqui ("YT", "TT",
  "in", "@"): sigla em bolinha colorida não é reconhecida, e num rodapé de página
  de vendas o logo é justamente o que faz a pessoa clicar.

  As cores são as das marcas, e ficam fora do tema de propósito: verde de WhatsApp
  em roxo não é WhatsApp.
*/
export const REDE_ICONES: Record<string, { icon: React.ReactNode; cor: string }> = {
  instagram: { icon: <InstagramLogo size={20} />, cor: "linear-gradient(135deg,#9333ea,#ec4899)" },
  whatsapp: { icon: <WhatsappLogo size={20} />, cor: "#16a34a" },
  facebook: { icon: <FacebookLogo size={20} />, cor: "#2563eb" },
  youtube: { icon: <YoutubeLogo size={20} />, cor: "#dc2626" },
  tiktok: { icon: <TiktokLogo size={20} />, cor: "#0F172A" },
  twitter: { icon: <XLogo size={20} />, cor: "#0F172A" },
  linkedin: { icon: <LinkedinLogo size={20} />, cor: "#1d4ed8" },
  email: { icon: <EnvelopeSimple size={20} />, cor: "#475569" },
}

function buildSocialUrl(rede: string, value: string): string {
  if (!value) return ""
  if (value.startsWith("http://") || value.startsWith("https://")) return value
  const clean = value.replace(/^@/, "").trim()
  switch (rede) {
    case "instagram":
      return `https://instagram.com/${clean}`
    case "whatsapp": {
      const digits = clean.replace(/\D/g, "")
      return `https://wa.me/${digits}`
    }
    case "facebook":
      return `https://facebook.com/${clean}`
    case "youtube":
      return clean.startsWith("@") ? `https://youtube.com/${clean}` : `https://youtube.com/@${clean}`
    case "tiktok":
      return `https://tiktok.com/@${clean}`
    case "twitter":
      return `https://x.com/${clean}`
    case "linkedin":
      return `https://linkedin.com/in/${clean}`
    case "email":
      return `mailto:${clean}`
    default:
      return value
  }
}

function RedesSociaisPreview({ block, theme, isLive }: { block: PageBlock; theme: PageTheme; isLive?: boolean }) {
  const itens = block.props.itens || []

  return (
    <SecaoDaPagina >
      <div className="flex justify-center gap-3 flex-wrap">
        {itens.map((item: any, i: number) => {
          const rede = REDE_ICONES[item.rede] || REDE_ICONES.instagram
          const href = isLive && item.url ? buildSocialUrl(item.rede, item.url) : undefined

          return (
            <a
              key={i}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className={`w-11 h-11 rounded-full flex items-center justify-center text-white transition-transform active:scale-90 ${!isLive || !item.url ? "opacity-60 pointer-events-none" : ""}`}
              style={{ background: rede.cor }}
            >
              {rede.icon}
            </a>
          )
        })}
      </div>
    </SecaoDaPagina>
  )
}

// ─── Checkout (preview no editor) ───

function CheckoutPreview({
  block,
  theme,
  precos,
}: {
  block: PageBlock
  theme: PageTheme
  precos?: { nome: string; valor: number }[]
}) {
  const radius = radiusValue(theme.radius)
  /* O primeiro é o que a página mostra em destaque, e é o que a prévia desenha. */
  const principal = precos?.[0]
  const outros = (precos?.length || 0) - 1
  return (
    <SecaoDaPagina respiro="mais" id="checkout" className="text-center" >
      <div
        className="p-6 space-y-3"
        style={{ background: `${theme.primary}14`, border: `2px solid ${theme.primary}40`, borderRadius: radius }}
      >
        <TextoDaPagina theme={theme} papel="secao" como="h2" fonte={fontClass(theme.font_heading)}>
          {block.props.titulo || "Adquira agora"}
        </TextoDaPagina>
        {principal ? (
          <>
            {/* O preço é o maior número da página: papel de promessa, na cor
                de destaque do personal. */}
            <TextoDaPagina
              theme={theme}
              papel="promessa"
              como="p"
              cor={theme.primary === theme.background ? theme.foreground : theme.primary}
            >
              {principal.valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
            </TextoDaPagina>
            {outros > 0 && (
              <TextoDaPagina theme={theme} papel="apoio">
                {outros === 1 ? "e mais uma opção de pagamento" : `e mais ${outros} opções de pagamento`}
              </TextoDaPagina>
            )}
          </>
        ) : (
          /* Sem preço marcado o botão não aparece na página no ar: a prévia diz
             isso, em vez de desenhar um botão que ninguém vai ver. */
          <TextoDaPagina theme={theme} papel="apoio">
            Nenhum preço marcado. Marque um ali em cima para o botão aparecer.
          </TextoDaPagina>
        )}
        <BotaoDaPagina theme={theme} tamanho="grande" className={fontClass(theme.font_body)}>
          {block.props.texto_botao || "Comprar"}
        </BotaoDaPagina>
        <TextoDaPagina theme={theme} papel="micro">
          Pagamento no Pix ou no cartão, com segurança
        </TextoDaPagina>
      </div>
    </SecaoDaPagina>
  )
}
